import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { FC } from 'react';
import {
  Card, Tabs, Select, Input, Row, Col,
  message, Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  ContainerOutlined,
  HeartOutlined,
  AlertOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { monitorViewApi, brandApi, keyNodeApi, matchNoteApi } from '../api/services';
import type {
  RiskRankingItemVO,
  MatrixSummaryVO,
  MatrixCellVO,
  NodeAnalysisSummaryVO,
  NodeAnalysisProjectDetailVO,
  NodeAnalysisProjectsPagedVO,
  ProjectMonitorDetailVO,
  ProjectNodeStatusVO,
  MatchNoteVO,
  KeyNodeVO,
  BrandVO,
} from '../api/types';
import './ProjectMonitorDetail.css';
import { ProjectCodeDisplay } from '../components/ProjectCodeDisplay';
import { useResizableColumns } from '../hooks/useResizableColumns';
import {
  IpcButton,
  IpcEmpty,
  IpcFilterToolbar,
  IpcFormField,
  IpcModal,
  IpcStatusBadge,
  IpcTable,
  IpcTag,
  IpcNodeStatus,
  IPC_FILTER_MULTI_SELECT_PROPS,
  IPC_FILTER_SELECT_POPUP_PROPS,
} from '../components/ui';
import { normalizeNodeProgress, parseNodeStatus } from '../utils/nodeStatus';
import { milestoneStatusLabel, milestoneStatusOptions } from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import { useListViewQuery, type ListViewCoords } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';

// ===== 状态渲染工具 =====

const statusLabelMap: Record<string, string> = {
  get completed() { return milestoneStatusLabel('completed'); },
  get in_progress() { return milestoneStatusLabel('in_progress'); },
  get pending() { return milestoneStatusLabel('pending'); },
  get terminated() { return milestoneStatusLabel('terminated'); },
  delayed: '延期',
  risk: '风险',
};

function renderProgressStatusBadge(status: string, className?: string) {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'risk') {
    return (
      <IpcStatusBadge variant="warning" className={className}>
        {statusLabelMap.risk}
      </IpcStatusBadge>
    );
  }
  const { progress, time } = parseNodeStatus(status);
  return (
    <IpcNodeStatus progress={progress} time={time} compact className={className} />
  );
}

function renderProgressStatus(status: string) {
  return renderProgressStatusBadge(status);
}

function renderRiskIndicator(flag: string) {
  const map: Record<string, { color: string; label: string }> = {
    normal: { color: 'var(--ipc-status-completed)', label: '正常' },
    delayed: { color: 'var(--ipc-status-delayed)', label: '延期' },
    risk: { color: 'var(--color-warning)', label: '风险' },
  };
  const item = map[(flag ?? '').trim().toLowerCase()] ?? map.normal;
  return (
    <span className="pmd-risk-indicator">
      <span className="pmd-risk-dot" style={{ background: item.color }} />
      {item.label}
    </span>
  );
}

function renderProjectLink(
  label: string,
  projectCode: string,
  onProjectClick: (code: string) => void,
  className = 'pmd-project-link',
) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => onProjectClick(projectCode)}
    >
      {label}
    </button>
  );
}

function renderMatrixCell(nodeStatus: string, riskFlag: string) {
  const progress = normalizeNodeProgress(nodeStatus);
  if (!nodeStatus || (nodeStatus ?? '').trim().toLowerCase() === 'empty') {
    return <span className="pmd-matrix-cell pmd-matrix-cell--empty">—</span>;
  }

  const riskKey = (riskFlag ?? '').trim().toLowerCase();
  const time = riskKey === 'delayed' ? 'delayed' as const : 'normal' as const;

  return (
    <span className="pmd-matrix-cell">
      <IpcNodeStatus progress={progress} time={time} compact className="pmd-matrix-cell__status" />
    </span>
  );
}

function renderDataSourceTag(source: string) {
  if (source === 'IPC') {
    return <IpcTag variant="primary">IPC</IpcTag>;
  }
  if (source === 'IPD') {
    return <IpcTag variant="outline">IPD</IpcTag>;
  }
  return <span>—</span>;
}

function formatDate(v?: string) {
  if (!v) return '—';
  return v.slice(0, 10);
}

// ===== 风险排行 =====

type RiskFilters = {
  brand: string;
  projectCode: string;
  projectName: string;
};

const DEFAULT_RISK_FILTERS: RiskFilters = {
  brand: 'all',
  projectCode: '',
  projectName: '',
};

const RiskRankingTab: FC<{
  brandOptions: { value: string; label: string }[];
  onProjectClick: (code: string) => void;
}> = ({ brandOptions, onProjectClick }) => {
  const [data, setData] = useState<RiskRankingItemVO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const {
    draft: riskDraft,
    patchDraft: patchRiskDraft,
    applied: riskApplied,
    applyIfChanged: applyRiskIfChanged,
    patchAndApply: patchAndApplyRisk,
    reset: resetRiskQuery,
    patchApplied: patchRiskApplied,
    page: pageNum,
    pageSize,
    setPagination: setRiskPagination,
  } = useListViewQuery<RiskFilters>({
    listViewKey: LIST_VIEW_KEYS.monitorRisk,
    defaultFilters: DEFAULT_RISK_FILTERS,
    emptyFilters: DEFAULT_RISK_FILTERS,
  });
  const draftBrand = riskDraft.brand;
  const draftProjectCode = riskDraft.projectCode;
  const draftProjectName = riskDraft.projectName;
  const appliedBrand = riskApplied.brand;
  const appliedProjectCode = riskApplied.projectCode;
  const appliedProjectName = riskApplied.projectName;

  const load = useCallback(async (overrides?: {
    brand?: string;
    projectCode?: string;
    projectName?: string;
    pageNum?: number;
    pageSize?: number;
  }) => {
    const brandVal = overrides?.brand ?? appliedBrand;
    const codeVal = overrides?.projectCode ?? appliedProjectCode;
    const nameVal = overrides?.projectName ?? appliedProjectName;
    const page = overrides?.pageNum ?? pageNum;
    const size = overrides?.pageSize ?? pageSize;
    setLoading(true);
    try {
      const res = await monitorViewApi.getRiskRanking({
        brand: brandVal !== 'all' ? brandVal : undefined,
        projectCode: codeVal || undefined,
        projectName: nameVal || undefined,
        pageNum: page,
        pageSize: size,
      });
      setData((res.list ?? []).map(item => {
        const raw = item as RiskRankingItemVO & { IpdProjectCodes?: string[] };
        return {
          ...item,
          ipdProjectCodes: raw.ipdProjectCodes ?? raw.IpdProjectCodes ?? [],
        };
      }));
      setTotal(res.total ?? 0);
    } catch {
      message.error('加载风险排行失败');
    } finally {
      setLoading(false);
    }
  }, [appliedBrand, appliedProjectCode, appliedProjectName, pageNum, pageSize]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 首屏加载
  }, []);

  const loadIfApplied = (coords: ListViewCoords<RiskFilters> | null) => {
    if (!coords) return;
    void load({
      brand: coords.applied.brand,
      projectCode: coords.applied.projectCode,
      projectName: coords.applied.projectName,
      pageNum: coords.page,
      pageSize: coords.pageSize,
    });
  };

  const applyFilters = () => {
    loadIfApplied(applyRiskIfChanged({
      brand: riskDraft.brand,
      projectCode: riskDraft.projectCode.trim(),
      projectName: riskDraft.projectName.trim(),
    }));
  };

  const clearAllRiskFilters = () => {
    loadIfApplied(resetRiskQuery());
  };
  const resetRiskFiltersToDefault = clearAllRiskFilters;

  const brandNameMap = useMemo(
    () => new Map(brandOptions.map(b => [b.value, b.label])),
    [brandOptions],
  );

  const riskAppliedChips = [
    appliedBrand !== 'all'
      ? { key: 'brand', label: `品牌: ${brandNameMap.get(appliedBrand) ?? appliedBrand}` }
      : null,
    appliedProjectCode.trim()
      ? { key: 'projectCode', label: `项目编号: ${appliedProjectCode.trim()}` }
      : null,
    appliedProjectName.trim()
      ? { key: 'projectName', label: `项目名称: ${appliedProjectName.trim()}` }
      : null,
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeRiskAppliedChip = (key: string) => {
    if (key === 'brand') loadIfApplied(patchRiskApplied({ brand: 'all' }));
    else if (key === 'projectCode') loadIfApplied(patchRiskApplied({ projectCode: '' }));
    else if (key === 'projectName') loadIfApplied(patchRiskApplied({ projectName: '' }));
  };

  const baseColumns: ColumnsType<RiskRankingItemVO> = useMemo(() => [
    {
      title: '品牌', dataIndex: 'brand', key: 'brand', width: 100,
      render: (code: string) => brandNameMap.get(code) ?? code,
    },
    {
      title: '项目编号', dataIndex: 'projectCode', key: 'projectCode', width: 160,
      render: (_: string, record: RiskRankingItemVO) => (
        <ProjectCodeDisplay
          codes={record.ipdProjectCodes}
          fallback={record.projectCode}
          link
          onClick={() => onProjectClick(record.projectCode)}
        />
      ),
    },
    {
      title: '项目名称', dataIndex: 'projectName', key: 'projectName', width: 200, ellipsis: true,
    },
    {
      title: '关键节点', dataIndex: 'totalNodes', key: 'totalNodes', width: 100, align: 'center' as const,
      render: (v: number) => <span className="pmd-count-unit">{v ?? 0} 个</span>,
    },
    {
      title: '延期节点', dataIndex: 'delayedNodes', key: 'delayedNodes', width: 100, align: 'center' as const,
      render: (v: number) => (
        <span className="pmd-count-unit">
          <span className={v > 0 ? 'pmd-count-num--delayed' : undefined}>{v ?? 0}</span> 个
        </span>
      ),
    },
    {
      title: '风险节点', dataIndex: 'riskNodes', key: 'riskNodes', width: 100, align: 'center' as const,
      render: (v: number) => (
        <span className="pmd-count-unit">
          <span className={v > 0 ? 'pmd-count-num--risk' : undefined}>{v ?? 0}</span> 个
        </span>
      ),
    },
    {
      title: '最大延期天数', dataIndex: 'maxDelayedDays', key: 'maxDelayedDays', width: 120, align: 'center' as const,
      render: (v: number | undefined) => {
        if (v == null || v === 0) return <span className="pmd-delay-days">—</span>;
        return <span className="pmd-delay-days">{v} 天</span>;
      },
    },
  ], [brandNameMap, onProjectClick]);

  const riskRankingTable = useResizableColumns(baseColumns);

  return (
    <Card className="pmd-risk-ranking-card pmd-tab-panel-card">
      <IpcFilterToolbar className="pmd-risk-filter-row" onSubmit={applyFilters} onReset={resetRiskFiltersToDefault} chipItems={riskAppliedChips} onRemoveChip={removeRiskAppliedChip} onClearChips={clearAllRiskFilters}>
        <Select
          value={draftBrand}
          onChange={v => loadIfApplied(patchAndApplyRisk({ brand: v ?? 'all' }))}
          options={[{ value: 'all', label: '全部品牌' }, ...brandOptions]}
          placeholder="全部品牌"
          className="pmd-filter-brand"
          {...IPC_FILTER_SELECT_POPUP_PROPS}
        />
        <Input
          placeholder="项目编号"
          value={draftProjectCode}
          onChange={e => patchRiskDraft({ projectCode: e.target.value })}
          onPressEnter={applyFilters}
          allowClear
          className="pmd-filter-code"
        />
        <Input
          placeholder="项目名称"
          value={draftProjectName}
          onChange={e => patchRiskDraft({ projectName: e.target.value })}
          onPressEnter={applyFilters}
          allowClear
          className="pmd-filter-name"
        />
      </IpcFilterToolbar>
      <IpcTable<RiskRankingItemVO>
        listViewKey={LIST_VIEW_KEYS.monitorRisk}
        panelClassName="pmd-table-panel"
        scrollClassName="pmd-table-body"
        rowKey="projectCode"
        columns={riskRankingTable.columns}
        components={riskRankingTable.components}
        tableLayout={riskRankingTable.tableLayout}
        dataSource={data}
        loading={loading}
        size="small"
        className={`pmd-risk-table ${riskRankingTable.tableClassName}`}
        autoHeight
        autoHeightDeps={[data.length, pageNum, pageSize, loading]}
        scroll={riskRankingTable.getScroll()}
        pagination={{
          className: 'pmd-table-pagination',
          current: pageNum,
          pageSize,
          total,
          onChange: (p, ps) => {
            const coords = setRiskPagination(p, ps);
            void load({ pageNum: coords.page, pageSize: coords.pageSize });
          },
        }}
      />
    </Card>
  );
};

// ===== 项目情况分析（矩阵） =====

const matrixProgressFilterOptions = () => {
  const fromBase = milestoneStatusOptions();
  return fromBase.length > 0
    ? fromBase
    : [
        { value: 'completed', label: milestoneStatusLabel('completed') },
        { value: 'in_progress', label: milestoneStatusLabel('in_progress') },
        { value: 'pending', label: milestoneStatusLabel('pending') },
        { value: 'terminated', label: milestoneStatusLabel('terminated') },
      ];
};

const matrixRiskFilterOptions = [
  { value: 'normal', label: '正常' },
  { value: 'risk', label: '风险' },
  { value: 'delayed', label: '延期' },
];

const matrixProgressColorMap: Record<string, string> = {
  completed: 'var(--ipc-status-completed)',
  in_progress: 'var(--ipc-status-in-progress)',
  pending: 'var(--ipc-status-pending)',
  terminated: 'var(--ipc-status-terminated)',
};

type MatrixFilters = {
  keyword: string;
  nodeId: number | 'all';
  progress: string[];
  riskFlags: string[];
};

const DEFAULT_MATRIX_FILTERS: MatrixFilters = {
  keyword: '',
  nodeId: 'all',
  progress: [],
  riskFlags: [],
};

const MatrixTab: FC<{
  onProjectClick: (code: string) => void;
}> = ({ onProjectClick }) => {
  const [data, setData] = useState<MatrixSummaryVO | null>(null);
  const [keyNodes, setKeyNodes] = useState<KeyNodeVO[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    draft: matrixDraft,
    patchDraft: patchMatrixDraft,
    applied: matrixApplied,
    applyIfChanged: applyMatrixIfChanged,
    patchAndApply: patchAndApplyMatrix,
    reset: resetMatrixQuery,
    patchApplied: patchMatrixApplied,
    page: pageNum,
    pageSize,
    setPagination: setMatrixPagination,
  } = useListViewQuery<MatrixFilters>({
    listViewKey: LIST_VIEW_KEYS.monitorMatrix,
    defaultFilters: DEFAULT_MATRIX_FILTERS,
    emptyFilters: DEFAULT_MATRIX_FILTERS,
  });
  const draftKeyword = matrixDraft.keyword;
  const draftNodeId = matrixDraft.nodeId;
  const draftProgress = matrixDraft.progress;
  const draftRiskFlags = matrixDraft.riskFlags;
  const appliedKeyword = matrixApplied.keyword;
  const appliedNodeId = matrixApplied.nodeId;
  const appliedProgress = matrixApplied.progress;
  const appliedRiskFlags = matrixApplied.riskFlags;

  useEffect(() => {
    (async () => {
      try {
        const res = await keyNodeApi.getList();
        const list = (res.list ?? []).filter(n => n.monitorEnabled);
        setKeyNodes(list);
      } catch {
        // ignore
      }
    })();
  }, []);

  const load = useCallback(async (overrides?: {
    keyword?: string;
    keyNodeId?: number | 'all';
    nodeStatuses?: string[];
    riskFlags?: string[];
    pageNum?: number;
    pageSize?: number;
  }) => {
    const keywordVal = overrides?.keyword ?? appliedKeyword;
    const nodeIdVal = overrides?.keyNodeId ?? appliedNodeId;
    const progressVal = overrides?.nodeStatuses ?? appliedProgress;
    const riskVal = overrides?.riskFlags ?? appliedRiskFlags;
    const page = overrides?.pageNum ?? pageNum;
    const size = overrides?.pageSize ?? pageSize;
    setLoading(true);
    try {
      const res = await monitorViewApi.getMatrix({
        keyword: keywordVal || undefined,
        keyNodeIds: nodeIdVal !== 'all' ? [nodeIdVal] : undefined,
        nodeStatuses: progressVal.length > 0 ? progressVal : undefined,
        riskFlags: riskVal.length > 0 ? riskVal : undefined,
        pageNum: page,
        pageSize: size,
      });
      setData({
        ...res,
        projects: (res.projects ?? []).map(p => {
          const raw = p as typeof p & { IpdProjectCodes?: string[] };
          return {
            ...p,
            ipdProjectCodes: raw.ipdProjectCodes ?? raw.IpdProjectCodes ?? [],
          };
        }),
      });
    } catch {
      message.error('加载项目情况分析失败');
    } finally {
      setLoading(false);
    }
  }, [appliedKeyword, appliedNodeId, appliedProgress, appliedRiskFlags, pageNum, pageSize]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 首屏
  }, []);

  const loadIfApplied = (coords: ListViewCoords<MatrixFilters> | null) => {
    if (!coords) return;
    void load({
      keyword: coords.applied.keyword,
      keyNodeId: coords.applied.nodeId,
      nodeStatuses: coords.applied.progress,
      riskFlags: coords.applied.riskFlags,
      pageNum: coords.page,
      pageSize: coords.pageSize,
    });
  };

  const applyMatrixFilters = () => {
    loadIfApplied(applyMatrixIfChanged({
      ...matrixDraft,
      keyword: matrixDraft.keyword.trim(),
    }));
  };

  const clearAllMatrixFilters = () => {
    loadIfApplied(resetMatrixQuery());
  };
  const resetMatrixFiltersToDefault = clearAllMatrixFilters;

  const matrixAppliedChips = [
    appliedKeyword.trim() ? { key: 'keyword', label: `关键词: ${appliedKeyword.trim()}` } : null,
    appliedNodeId !== 'all'
      ? {
          key: 'nodeId',
          label: `关键节点: ${keyNodes.find(n => n.id === appliedNodeId)?.name ?? appliedNodeId}`,
        }
      : null,
    ...appliedProgress.map(status => ({
      key: `progress:${status}`,
      label: `节点状态: ${matrixProgressFilterOptions().find(o => o.value === status)?.label ?? status}`,
    })),
    ...appliedRiskFlags.map(flag => ({
      key: `risk:${flag}`,
      label: `风险: ${matrixRiskFilterOptions.find(o => o.value === flag)?.label ?? flag}`,
    })),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeMatrixAppliedChip = (key: string) => {
    if (key === 'keyword') {
      loadIfApplied(patchMatrixApplied({ keyword: '' }));
      return;
    }
    if (key === 'nodeId') {
      loadIfApplied(patchMatrixApplied({ nodeId: 'all' }));
      return;
    }
    if (key.startsWith('progress:')) {
      const status = key.slice('progress:'.length);
      loadIfApplied(patchMatrixApplied({
        progress: appliedProgress.filter(s => s !== status),
      }));
      return;
    }
    if (key.startsWith('risk:')) {
      const flag = key.slice('risk:'.length);
      loadIfApplied(patchMatrixApplied({
        riskFlags: appliedRiskFlags.filter(f => f !== flag),
      }));
    }
  };

  const matrixMap = new Map<number, Map<number, MatrixCellVO>>();
  (data?.matrix ?? []).forEach(item => {
    if (!matrixMap.has(item.projectId)) matrixMap.set(item.projectId, new Map());
    matrixMap.get(item.projectId)!.set(item.nodeId, item);
  });

  const nodes = data?.nodes ?? [];
  const projects = data?.projects ?? [];
  const keyNodeFilterOptions = useMemo(
    () => [
      { value: 'all' as const, label: '全部关键节点' },
      ...keyNodes.map(n => ({ value: n.id, label: n.name })),
    ],
    [keyNodes],
  );

  const baseColumns: ColumnsType<{ projectId: number; projectCode: string; ipdProjectCodes?: string[]; projectName: string; brand: string }> = useMemo(() => [
    {
      title: '项目信息',
      key: 'projectInfo',
      width: 200,
      fixed: 'left',
      render: (_: unknown, record) => (
        <div className="pmd-matrix-project-info">
          {renderProjectLink(
            record.projectName,
            record.projectCode,
            onProjectClick,
            'pmd-project-link pmd-matrix-project-info__name',
          )}
          <ProjectCodeDisplay
            codes={record.ipdProjectCodes}
            fallback={record.projectCode}
            className="pmd-matrix-project-info__code"
            link
            onClick={() => onProjectClick(record.projectCode)}
          />
        </div>
      ),
    },
    ...nodes.map(node => ({
      title: node.nodeName,
      key: `node-${node.nodeId}`,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, record: { projectId: number }) => {
        const item = matrixMap.get(record.projectId)?.get(node.nodeId);
        if (!item) return renderMatrixCell('empty', 'empty');
        return renderMatrixCell(item.nodeStatus, item.riskFlag);
      },
    })),
  ], [nodes, matrixMap, onProjectClick]);

  const matrixTable = useResizableColumns(baseColumns);

  return (
    <Card className="pmd-matrix-card pmd-tab-panel-card">
      <IpcFilterToolbar
        className="pmd-matrix-filter-row"
        onSubmit={applyMatrixFilters}
        onReset={resetMatrixFiltersToDefault}
        chipItems={matrixAppliedChips}
        onRemoveChip={removeMatrixAppliedChip}
        onClearChips={clearAllMatrixFilters}
      >
        <Input
          placeholder="项目编码/名称"
          value={draftKeyword}
          onChange={e => patchMatrixDraft({ keyword: e.target.value })}
          onPressEnter={applyMatrixFilters}
          allowClear
          className="pmd-matrix-filter-keyword"
        />
        <Select
          value={draftNodeId}
          onChange={v => loadIfApplied(patchAndApplyMatrix({ nodeId: v ?? 'all' }))}
          options={keyNodeFilterOptions}
          placeholder="全部关键节点"
          className="pmd-matrix-filter-node"
          {...IPC_FILTER_SELECT_POPUP_PROPS}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="节点状态"
          value={draftProgress}
          onChange={v => loadIfApplied(patchAndApplyMatrix({ progress: v }))}
          options={matrixProgressFilterOptions()}
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          className="pmd-matrix-filter-status"
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="风险标识"
          value={draftRiskFlags}
          onChange={v => loadIfApplied(patchAndApplyMatrix({ riskFlags: v }))}
          options={matrixRiskFilterOptions}
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          className="pmd-matrix-filter-risk"
        />
      </IpcFilterToolbar>

      <IpcTable<NonNullable<MatrixSummaryVO['projects']>[number]>
        listViewKey={LIST_VIEW_KEYS.monitorMatrix}
        panelClassName="pmd-table-panel"
        scrollClassName="pmd-table-body"
        rowKey="projectId"
        columns={matrixTable.columns}
        components={matrixTable.components}
        tableLayout={matrixTable.tableLayout}
        dataSource={projects}
        loading={loading}
        size="small"
        className={`pmd-matrix-table ${matrixTable.tableClassName}`}
        autoHeight
        autoHeightDeps={[projects.length, pageNum, pageSize, loading]}
        scroll={matrixTable.getScroll()}
        pagination={{
          className: 'pmd-table-pagination',
          current: pageNum,
          pageSize,
          total: data?.total ?? 0,
          onChange: (p, ps) => {
            const coords = setMatrixPagination(p, ps);
            void load({ pageNum: coords.page, pageSize: coords.pageSize });
          },
        }}
      />
    </Card>
  );
};

// ===== 关键节点分析 =====

const nodeAnalysisCountColors = {
  total: 'var(--ipc-status-in-progress)',
  normal: 'var(--ipc-status-completed)',
  risk: 'var(--color-warning)',
  delayed: 'var(--ipc-status-delayed)',
};

function renderNodeAnalysisCount(count: number, color: string, onClick?: () => void) {
  if (count <= 0) {
    return <span className="pmd-node-analysis-count pmd-node-analysis-count--zero">0</span>;
  }

  return (
    <button
      type="button"
      className="pmd-node-analysis-count"
      style={{ color }}
      onClick={onClick}
    >
      {count}个
    </button>
  );
}

function resolveBrandLabel(code: string, brandOptions: { value: string; label: string }[]) {
  return brandOptions.find(b => b.value === code)?.label ?? code ?? '—';
}

const modalRiskColorMap: Record<string, { color: string; label: string }> = {
  normal: { color: 'var(--ipc-status-completed)', label: '正常' },
  delayed: { color: 'var(--ipc-status-delayed)', label: '延期' },
  risk: { color: 'var(--color-warning)', label: '风险' },
};

function renderModalNodeStatus(status: string) {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'terminated') {
    return <IpcNodeStatus progress="terminated" compact className="pmd-node-analysis-modal__status" />;
  }
  const text = statusLabelMap[s] || status || '—';
  const color = matrixProgressColorMap[s] ?? 'var(--color-text)';
  return <span className="pmd-node-analysis-modal__status" style={{ color }}>{text}</span>;
}

function renderModalRiskIndicator(flag: string) {
  const item = modalRiskColorMap[(flag ?? '').trim().toLowerCase()] ?? modalRiskColorMap.normal;
  return (
    <span className="pmd-node-analysis-modal__risk">
      <span className="pmd-node-analysis-modal__risk-dot" style={{ background: item.color }} />
      <span style={{ color: item.color }}>{item.label}</span>
    </span>
  );
}

const NodeAnalysisTab: FC<{
  brandOptions: { value: string; label: string }[];
}> = ({ brandOptions }) => {
  const [summaryList, setSummaryList] = useState<NodeAnalysisSummaryVO[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalNode, setModalNode] = useState<NodeAnalysisSummaryVO | null>(null);
  const [modalRiskFlag, setModalRiskFlag] = useState<string | undefined>(undefined);
  const [modalDraftDataSource, setModalDraftDataSource] = useState<string>('all');
  const [modalDraftKeyword, setModalDraftKeyword] = useState('');
  const [modalDraftBrand, setModalDraftBrand] = useState<string>('all');
  const [modalAppliedDataSource, setModalAppliedDataSource] = useState<string>('all');
  const [modalAppliedKeyword, setModalAppliedKeyword] = useState('');
  const [modalAppliedBrand, setModalAppliedBrand] = useState<string>('all');
  const [modalPageNum, setModalPageNum] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(20);
  const [modalData, setModalData] = useState<NodeAnalysisProjectsPagedVO | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const modalRequestSeq = useRef(0);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await monitorViewApi.getNodeAnalysis();
      setSummaryList(res ?? []);
    } catch {
      message.error('加载关键节点分析失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  const fetchModalProjects = useCallback(async (
    keyNodeId: number,
    options: {
      riskFlag?: string;
      brand?: string;
      keyword?: string;
      dataSource?: string;
      pageNum?: number;
      pageSize?: number;
    },
  ) => {
    const requestSeq = ++modalRequestSeq.current;
    setModalLoading(true);
    try {
      const res = await monitorViewApi.getNodeAnalysisProjects({
        keyNodeId,
        brand: options.brand && options.brand !== 'all' ? options.brand : undefined,
        keyword: options.keyword || undefined,
        dataSource: options.dataSource && options.dataSource !== 'all' ? options.dataSource : undefined,
        riskFlag: options.riskFlag,
        pageNum: options.pageNum ?? 1,
        pageSize: options.pageSize ?? 20,
      });
      if (requestSeq !== modalRequestSeq.current) return;
      setModalData(res);
    } catch {
      if (requestSeq !== modalRequestSeq.current) return;
      message.error('加载项目明细失败');
    } finally {
      if (requestSeq === modalRequestSeq.current) {
        setModalLoading(false);
      }
    }
  }, []);

  const runModalFetch = useCallback((
    overrides?: {
      brand?: string;
      keyword?: string;
      dataSource?: string;
      riskFlag?: string | null;
      pageNum?: number;
      pageSize?: number;
    },
  ) => {
    if (!modalNode) return;
    void fetchModalProjects(modalNode.keyNodeId, {
      riskFlag: overrides && 'riskFlag' in overrides
        ? (overrides.riskFlag ?? undefined)
        : modalRiskFlag,
      brand: overrides?.brand ?? modalAppliedBrand,
      keyword: overrides?.keyword ?? modalAppliedKeyword,
      dataSource: overrides?.dataSource ?? modalAppliedDataSource,
      pageNum: overrides?.pageNum ?? modalPageNum,
      pageSize: overrides?.pageSize ?? modalPageSize,
    });
  }, [
    modalNode,
    modalRiskFlag,
    modalAppliedBrand,
    modalAppliedKeyword,
    modalAppliedDataSource,
    modalPageNum,
    modalPageSize,
    fetchModalProjects,
  ]);

  const commitModalFilters = (next: { dataSource: string; keyword: string; brand: string }) => {
    if (
      next.dataSource === modalAppliedDataSource
      && next.keyword === modalAppliedKeyword
      && next.brand === modalAppliedBrand
    ) return;
    setModalDraftDataSource(next.dataSource);
    setModalAppliedDataSource(next.dataSource);
    setModalAppliedKeyword(next.keyword);
    setModalDraftBrand(next.brand);
    setModalAppliedBrand(next.brand);
    setModalPageNum(1);
    runModalFetch({ dataSource: next.dataSource, keyword: next.keyword, brand: next.brand, pageNum: 1 });
  };

  const applyModalFilters = () => {
    commitModalFilters({
      dataSource: modalDraftDataSource,
      keyword: modalDraftKeyword.trim(),
      brand: modalDraftBrand,
    });
  };

  const clearAllModalFilters = () => {
    setModalDraftDataSource('all');
    setModalDraftKeyword('');
    setModalDraftBrand('all');
    setModalAppliedDataSource('all');
    setModalAppliedKeyword('');
    setModalAppliedBrand('all');
    setModalPageNum(1);
    runModalFetch({ dataSource: 'all', keyword: '', brand: 'all', pageNum: 1 });
  };
  const modalBrandLabel = brandOptions.find(b => b.value === modalAppliedBrand)?.label ?? modalAppliedBrand;
  const modalAppliedChips = [
    modalRiskFlag
      ? {
          key: 'riskFlag',
          label: `风险类型: ${matrixRiskFilterOptions.find(o => o.value === modalRiskFlag)?.label ?? modalRiskFlag}`,
        }
      : null,
    modalAppliedDataSource !== 'all'
      ? { key: 'dataSource', label: `数据源: ${modalAppliedDataSource}` }
      : null,
    modalAppliedKeyword.trim()
      ? { key: 'keyword', label: `关键词: ${modalAppliedKeyword.trim()}` }
      : null,
    modalAppliedBrand !== 'all'
      ? { key: 'brand', label: `品牌: ${modalBrandLabel}` }
      : null,
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeModalAppliedChip = (key: string) => {
    if (key === 'riskFlag') {
      setModalRiskFlag(undefined);
      setModalPageNum(1);
      runModalFetch({ riskFlag: null, pageNum: 1 });
      return;
    }
    if (key === 'dataSource') {
      setModalDraftDataSource('all');
      setModalAppliedDataSource('all');
      setModalPageNum(1);
      runModalFetch({ dataSource: 'all', pageNum: 1 });
      return;
    }
    if (key === 'keyword') {
      setModalDraftKeyword('');
      setModalAppliedKeyword('');
      setModalPageNum(1);
      runModalFetch({ keyword: '', pageNum: 1 });
      return;
    }
    if (key === 'brand') {
      setModalDraftBrand('all');
      setModalAppliedBrand('all');
      setModalPageNum(1);
      runModalFetch({ brand: 'all', pageNum: 1 });
    }
  };

  const openProjectModal = (node: NodeAnalysisSummaryVO, riskFlag?: string) => {
    setModalData(null);
    setModalNode(node);
    setModalRiskFlag(riskFlag);
    setModalDraftDataSource('all');
    setModalDraftKeyword('');
    setModalDraftBrand('all');
    setModalAppliedDataSource('all');
    setModalAppliedKeyword('');
    setModalAppliedBrand('all');
    setModalPageNum(1);
    setModalPageSize(20);
    setModalOpen(true);

    void fetchModalProjects(node.keyNodeId, {
      riskFlag,
      brand: 'all',
      keyword: '',
      dataSource: 'all',
      pageNum: 1,
      pageSize: 20,
    });
  };

  const summaryBaseColumns: ColumnsType<NodeAnalysisSummaryVO> = useMemo(() => [
    {
      title: '关键节点',
      key: 'keyNode',
      width: 220,
      render: (_: unknown, record) => (
        <div className="pmd-node-analysis-node">
          <div className="pmd-node-analysis-node__name">{record.keyNodeName}</div>
          <div className="pmd-node-analysis-node__code">{record.keyNodeCode}</div>
        </div>
      ),
    },
    {
      title: '关联项目',
      dataIndex: 'totalProjects',
      key: 'totalProjects',
      width: 120,
      align: 'center',
      render: (v: number, record) => renderNodeAnalysisCount(
        v ?? 0,
        nodeAnalysisCountColors.total,
        () => openProjectModal(record),
      ),
    },
    {
      title: '正常',
      dataIndex: 'normalProjects',
      key: 'normalProjects',
      width: 100,
      align: 'center',
      render: (v: number, record) => renderNodeAnalysisCount(
        v ?? 0,
        nodeAnalysisCountColors.normal,
        () => openProjectModal(record, 'normal'),
      ),
    },
    {
      title: '风险',
      dataIndex: 'riskProjects',
      key: 'riskProjects',
      width: 100,
      align: 'center',
      render: (v: number, record) => renderNodeAnalysisCount(
        v ?? 0,
        nodeAnalysisCountColors.risk,
        () => openProjectModal(record, 'risk'),
      ),
    },
    {
      title: '延期',
      dataIndex: 'delayedProjects',
      key: 'delayedProjects',
      width: 100,
      align: 'center',
      render: (v: number, record) => renderNodeAnalysisCount(
        v ?? 0,
        nodeAnalysisCountColors.delayed,
        () => openProjectModal(record, 'delayed'),
      ),
    },
  ], [openProjectModal]);

  const summaryTable = useResizableColumns(summaryBaseColumns);

  const modalBaseColumns: ColumnsType<NodeAnalysisProjectDetailVO> = useMemo(() => [
    {
      title: '数据源',
      dataIndex: 'dataSource',
      key: 'dataSource',
      width: 80,
      render: (v: string) => renderDataSourceTag(v),
    },
    {
      title: '项目信息',
      key: 'projectInfo',
      width: 200,
      render: (_: unknown, record) => (
        <div className="pmd-matrix-project-info">
          <div className="pmd-matrix-project-info__name">{record.projectName}</div>
          <ProjectCodeDisplay
            codes={record.ipdProjectCodes}
            fallback={record.projectCode}
            className="pmd-matrix-project-info__code"
          />
        </div>
      ),
    },
    {
      title: '品牌',
      dataIndex: 'brand',
      key: 'brand',
      width: 140,
      ellipsis: true,
      render: (v: string) => resolveBrandLabel(v, brandOptions),
    },
    {
      title: '节点状态',
      dataIndex: 'nodeStatus',
      key: 'nodeStatus',
      width: 100,
      render: (v: string) => renderModalNodeStatus(v),
    },
    {
      title: '风险标识',
      dataIndex: 'riskFlag',
      key: 'riskFlag',
      width: 100,
      render: (v: string) => renderModalRiskIndicator(v),
    },
    {
      title: '计划开始',
      dataIndex: 'planStartDate',
      key: 'planStartDate',
      width: 110,
      render: (v: string) => <span className="pmd-node-analysis-modal__date">{formatDate(v)}</span>,
    },
    {
      title: '计划完成',
      dataIndex: 'planEndDate',
      key: 'planEndDate',
      width: 110,
      render: (v: string) => <span className="pmd-node-analysis-modal__date">{formatDate(v)}</span>,
    },
    {
      title: '实际完成',
      dataIndex: 'actualEndDate',
      key: 'actualEndDate',
      width: 110,
      render: (v: string) => <span className="pmd-node-analysis-modal__date">{formatDate(v)}</span>,
    },
  ], [brandOptions]);

  const modalTable = useResizableColumns(modalBaseColumns);

  return (
    <>
      <Card className="pmd-node-analysis-card pmd-tab-panel-card">
        <IpcTable<NodeAnalysisSummaryVO>
          panelClassName="pmd-table-panel"
          scrollClassName="pmd-table-body"
          rowKey="keyNodeId"
          columns={summaryTable.columns}
          components={summaryTable.components}
          tableLayout={summaryTable.tableLayout}
          dataSource={summaryList}
          loading={loading}
          size="small"
          className={`pmd-node-analysis-table ${summaryTable.tableClassName}`}
          autoHeight
          autoHeightDeps={[summaryList.length, loading]}
          scroll={summaryTable.getScroll()}
          pagination={false}
        />
      </Card>

      <IpcModal
        title="项目明细"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={1100}
        style={{ maxWidth: '96vw' }}
        className="pmd-node-analysis-modal"
      >
        <div className="ipc-table-viewport pmd-node-analysis-modal__viewport">
          <div className="ipc-table-viewport__top">
            <IpcFilterToolbar
              className="pmd-node-analysis-modal__filters"
              onSubmit={applyModalFilters}
              onReset={clearAllModalFilters}
              chipItems={modalAppliedChips}
              onRemoveChip={removeModalAppliedChip}
              onClearChips={clearAllModalFilters}
            >
              <Select
                value={modalDraftDataSource}
                onChange={v => {
                  commitModalFilters({
                    dataSource: v ?? 'all',
                    keyword: modalAppliedKeyword,
                    brand: modalDraftBrand,
                  });
                }}
                options={[
                  { value: 'all', label: '全部数据源' },
                  { value: 'IPC', label: 'IPC' },
                  { value: 'IPD', label: 'IPD' },
                ]}
                placeholder="全部数据源"
                className="pmd-node-analysis-modal__filter-source"
              />
              <Input
                placeholder="项目编码/名称"
                value={modalDraftKeyword}
                onChange={e => setModalDraftKeyword(e.target.value)}
                onPressEnter={applyModalFilters}
                allowClear
                className="pmd-node-analysis-modal__filter-keyword"
              />
              <Select
                value={modalDraftBrand}
                onChange={v => {
                  commitModalFilters({
                    dataSource: modalDraftDataSource,
                    keyword: modalAppliedKeyword,
                    brand: v ?? 'all',
                  });
                }}
                options={[{ value: 'all', label: '全部品牌' }, ...brandOptions]}
                placeholder="全部品牌"
                className="pmd-node-analysis-modal__filter-brand"
              />
            </IpcFilterToolbar>
          </div>

          <div className="ipc-table-viewport__table">
            <IpcTable<NodeAnalysisProjectDetailVO>
              panelClassName="pmd-node-analysis-modal__table-wrap"
              scrollClassName="pmd-node-analysis-modal__table-scroll"
              rowKey={r => `${r.projectId}-${r.dataSource}-${r.projectCode}`}
              columns={modalTable.columns}
              components={modalTable.components}
              tableLayout={modalTable.tableLayout}
              dataSource={modalData?.list ?? []}
              loading={modalLoading}
              bordered={false}
              className={`pmd-node-analysis-modal__table ${modalTable.tableClassName}`}
              // 弹框不用 autoHeight：表体固定高度，超出在表内滚动，分页留在表格外。
              scroll={modalTable.getScroll({ y: 500 })}
              pagination={{
                className: 'pmd-node-analysis-modal__pagination',
                current: modalPageNum,
                pageSize: modalPageSize,
                total: modalData?.total ?? 0,
                onChange: (p, ps) => {
                  setModalPageNum(p);
                  setModalPageSize(ps);
                  runModalFetch({ pageNum: p, pageSize: ps });
                },
              }}
              compact
            />
          </div>
        </div>
      </IpcModal>
    </>
  );
};

// ===== 项目详情页 =====

const ProjectDetailView: FC<{
  projectCode: string;
  onBack: () => void;
}> = ({ projectCode, onBack }) => {
  const [detail, setDetail] = useState<ProjectMonitorDetailVO | null>(null);
  const [loading, setLoading] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<ProjectNodeStatusVO | null>(null);
  const [notes, setNotes] = useState<MatchNoteVO[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await monitorViewApi.getProjectDetail(projectCode);
        const raw = res as ProjectMonitorDetailVO & { IpdProjectCodes?: string[] };
        setDetail({
          ...res,
          ipdProjectCodes: raw.ipdProjectCodes ?? raw.IpdProjectCodes ?? [],
        });
      } catch {
        message.error('加载项目详情失败');
      } finally {
        setLoading(false);
      }
    })();
  }, [projectCode]);

  const loadNotes = useCallback(async (keyNodeId: number) => {
    setNotesLoading(true);
    try {
      const list = await matchNoteApi.getList({ projectCode, keyNodeId });
      setNotes(list);
    } catch {
      message.error('加载节点说明失败');
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [projectCode]);

  const handleViewNotes = async (record: ProjectNodeStatusVO) => {
    setSelectedNode(record);
    setNoteContent('');
    setNoteModalOpen(true);
    await loadNotes(record.keyNodeId);
  };

  const handleAddNote = async () => {
    const content = noteContent.trim();
    if (!content) {
      message.warning('请输入节点说明');
      return;
    }
    if (!selectedNode) return;

    setSubmitting(true);
    try {
      await matchNoteApi.create({
        projectCode,
        keyNodeId: selectedNode.keyNodeId,
        content,
      });
      setNoteContent('');
      message.success('添加成功');
      await loadNotes(selectedNode.keyNodeId);
    } catch {
      message.error('添加节点说明失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!selectedNode) return;

    setDeletingNoteId(noteId);
    try {
      await matchNoteApi.delete(noteId);
      message.success('删除成功');
      await loadNotes(selectedNode.keyNodeId);
    } catch {
      message.error('删除失败，只能删除自己写的说明');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const detailBaseColumns: ColumnsType<ProjectNodeStatusVO> = useMemo(() => {
    const keyNodes = detail?.keyNodes ?? [];
    const getKeyNodeRowSpan = (record: ProjectNodeStatusVO, index: number) => {
      const firstIndex = keyNodes.findIndex(r => r.keyNodeId === record.keyNodeId);
      if (index !== firstIndex) return { rowSpan: 0 };
      const count = keyNodes.filter(r => r.keyNodeId === record.keyNodeId).length;
      return { rowSpan: count };
    };

    return [
      {
        title: '关键节点',
        dataIndex: 'keyNodeName',
        key: 'nodeName',
        width: 120,
        fixed: 'left' as const,
        onCell: (record, index) => getKeyNodeRowSpan(record, index ?? 0),
      },
      {
        title: '数据源',
        dataIndex: 'dataSource',
        key: 'dataSource',
        width: 80,
        render: (v: string) => renderDataSourceTag(v),
      },
      {
        title: '节点名称',
        dataIndex: 'mappedNodeName',
        key: 'mappedNodeName',
        width: 140,
        ellipsis: true,
        render: (v: string) => v || '—',
      },
      {
        title: '节点状态',
        dataIndex: 'nodeStatus',
        key: 'status',
        width: 90,
        render: (s: string) => renderProgressStatus(s),
      },
      {
        title: '风险标识',
        dataIndex: 'riskFlag',
        key: 'riskFlag',
        width: 100,
        render: (v: string) => renderRiskIndicator(v),
      },
      {
        title: '计划开始',
        dataIndex: 'planStartDate',
        key: 'planStartDate',
        width: 110,
        render: (v: string) => formatDate(v),
      },
      {
        title: '计划完成',
        dataIndex: 'planEndDate',
        key: 'planEndDate',
        width: 110,
        render: (v: string) => formatDate(v),
      },
      {
        title: '实际完成',
        dataIndex: 'actualEndDate',
        key: 'actualEndDate',
        width: 110,
        render: (v: string) => formatDate(v),
      },
      {
        title: '风险天数',
        dataIndex: 'riskDays',
        key: 'riskDays',
        width: 90,
        align: 'center' as const,
        render: (v: number | undefined, record) => {
          if (record.riskFlag !== 'risk' || v == null || v === 0) return '—';
          return <span className="pmd-risk-days">{v} 天</span>;
        },
      },
      {
        title: '延期天数',
        dataIndex: 'delayedDays',
        key: 'delayedDays',
        width: 90,
        align: 'center' as const,
        render: (v: number | undefined, record) => {
          if (record.riskFlag !== 'delayed' || v == null || v === 0) return '—';
          return <span className="pmd-delay-days-cell">{v} 天</span>;
        },
      },
      {
        title: '风险原因',
        dataIndex: 'riskReason',
        key: 'riskReason',
        width: 220,
        ellipsis: true,
        render: (v: string) => v || '—',
      },
      {
        title: '操作',
        key: 'actions',
        width: 80,
        fixed: 'right' as const,
        render: (_: unknown, record) => (
          <IpcButton variant="link" size="small" onClick={() => handleViewNotes(record)}>
            查看
          </IpcButton>
        ),
      },
    ];
  }, [detail?.keyNodes, handleViewNotes]);

  const detailTable = useResizableColumns(detailBaseColumns);

  if (loading) {
    return <div className="pmd-loading">加载中...</div>;
  }

  if (!detail) {
    return <IpcEmpty title="未找到项目数据" description="" />;
  }

  const keyNodes = detail.keyNodes ?? [];

  return (
    <div className="pmd-project-detail">
      <div className="pmd-detail-header">
        <IpcButton variant="link" className="pmd-detail-back" icon={<ArrowLeftOutlined />} onClick={onBack}>
          返回
        </IpcButton>
        <h2 className="pmd-detail-title">{detail.projectName}</h2>
        <div className="pmd-detail-meta">
          <span>项目编码：
            <ProjectCodeDisplay
              codes={detail.ipdProjectCodes}
              fallback={detail.projectCode}
            />
          </span>
          <span>品牌：{detail.brand || '—'}</span>
          <span>预计上市：{formatDate(detail.launchDate)}</span>
        </div>
      </div>

      <IpcTable<NonNullable<ProjectMonitorDetailVO['keyNodes']>[number]>
        panelClassName="pmd-detail-table-wrap"
        scrollClassName="pmd-detail-table-scroll"
        rowKey={row => `${row.keyNodeId}-${row.mappingId ?? row.dataSource}-${row.ipcNodeId ?? row.ipdStateKey ?? ''}`}
        dataSource={keyNodes}
        loading={false}
        size="small"
        pagination={false}
        columns={detailTable.columns}
        components={detailTable.components}
        tableLayout={detailTable.tableLayout}
        className={detailTable.tableClassName}
        autoHeight
        autoHeightDeps={[keyNodes.length, loading]}
        scroll={detailTable.getScroll()}
      />

      <IpcModal
        title="节点说明"
        open={noteModalOpen}
        okText="添加"
        cancelText="取消"
        confirmLoading={submitting}
        okDisabled={!noteContent.trim()}
        onOk={handleAddNote}
        onCancel={() => setNoteModalOpen(false)}
        width={560}
      >
        <div className="pmd-note-modal-body">
          <IpcFormField label="节点说明" required fullWidth>
            <Input.TextArea
              rows={4}
              placeholder="请输入节点说明"
              value={noteContent}
              onChange={e => setNoteContent(e.target.value)}
              maxLength={2000}
            />
          </IpcFormField>

          <IpcFormField label="说明记录" fullWidth>
            <div className="pmd-note-list">
              {notesLoading ? (
                <div className="pmd-loading">加载中...</div>
              ) : notes.length > 0 ? (
                notes.map(note => (
                  <div key={note.id} className="pmd-note-item">
                    <div className="pmd-note-item__dot" />
                    <div className="pmd-note-item__body">
                      <div className="pmd-note-item__header">
                        <div className="pmd-note-item__content">{note.content}</div>
                        {note.canDelete && (
                          <Popconfirm
                            title="确定删除这条说明吗？"
                            okText="删除"
                            cancelText="取消"
                            okButtonProps={{ danger: true, loading: deletingNoteId === note.id }}
                            onConfirm={() => handleDeleteNote(note.id)}
                          >
                            <IpcButton
                              className="pmd-note-item__delete"
                              variant="ghost"
                              size="small"
                              iconOnly
                              icon={<CloseOutlined />}
                              aria-label="删除说明"
                              disabled={deletingNoteId === note.id}
                            />
                          </Popconfirm>
                        )}
                      </div>
                      <div className="pmd-note-item__meta">
                        {note.createdBy || '未知'}
                        {note.createdAt ? ` · ${note.createdAt.slice(0, 16).replace('T', ' ')}` : ''}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <IpcEmpty title="暂无节点说明" description="" />
              )}
            </div>
          </IpcFormField>
        </div>
      </IpcModal>
    </div>
  );
};

// ===== 主监控视图页 =====

const ProjectMonitorDetail: FC = () => {
  useIpcStatusEnums();

  const [brandOptions, setBrandOptions] = useState<{ value: string; label: string }[]>([]);
  const [activeTab, setActiveTab] = useState('risk');
  const [detailReturnTab, setDetailReturnTab] = useState('risk');
  const [projectDetailCode, setProjectDetailCode] = useState<string | null>(null);

  const openProjectDetail = useCallback((code: string) => {
    setDetailReturnTab(activeTab);
    setProjectDetailCode(code);
  }, [activeTab]);
  const [summary, setSummary] = useState({ total: 0, normal: 0, risk: 0 });

  const loadSummary = useCallback(async () => {
    try {
      const res = await monitorViewApi.getRiskRanking({ pageNum: 1, pageSize: 9999 });
      const list = res.list ?? [];
      setSummary({
        total: res.total ?? list.length,
        normal: list.filter(p => (p.delayedNodes ?? 0) === 0 && (p.riskNodes ?? 0) === 0).length,
        risk: list.filter(p => (p.delayedNodes ?? 0) > 0 || (p.riskNodes ?? 0) > 0).length,
      });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const brands = await brandApi.getList();
        setBrandOptions((brands ?? []).map((b: BrandVO) => ({ value: b.code, label: b.name })));
      } catch {
        // ignore
      }
      void loadSummary();
    })();
  }, [loadSummary]);

  if (projectDetailCode) {
    return (
      <ProjectDetailView
        projectCode={projectDetailCode}
        onBack={() => {
          setProjectDetailCode(null);
          setActiveTab(detailReturnTab);
        }}
      />
    );
  }

  const tabItems = [
    {
      key: 'risk',
      label: <span>项目风险排行</span>,
      children: <RiskRankingTab brandOptions={brandOptions} onProjectClick={openProjectDetail} />,
    },
    {
      key: 'matrix',
      label: <span>项目情况分析</span>,
      children: <MatrixTab onProjectClick={openProjectDetail} />,
    },
    {
      key: 'node',
      label: <span>关键节点分析</span>,
      children: <NodeAnalysisTab brandOptions={brandOptions} />,
    },
  ];

  return (
    <div className="project-monitor-detail">
      <Row gutter={[16, 16]} className="pmd-summary-row">
        <Col xs={24} sm={8}>
          <div className="pmd-summary-card">
            <div className="pmd-summary-card__left">
              <div className="pmd-summary-card__icon pmd-summary-card__icon--total">
                <ContainerOutlined style={{ fontSize: 20 }} />
              </div>
              <span className="pmd-summary-card__label">监控总数</span>
            </div>
            <span className="pmd-summary-card__value">{summary.total}</span>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="pmd-summary-card">
            <div className="pmd-summary-card__left">
              <div className="pmd-summary-card__icon pmd-summary-card__icon--normal">
                <HeartOutlined style={{ fontSize: 20 }} />
              </div>
              <span className="pmd-summary-card__label">正常项目</span>
            </div>
            <span className="pmd-summary-card__value">{summary.normal}</span>
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div className="pmd-summary-card">
            <div className="pmd-summary-card__left">
              <div className="pmd-summary-card__icon pmd-summary-card__icon--risk">
                <AlertOutlined style={{ fontSize: 20 }} />
              </div>
              <span className="pmd-summary-card__label">风险项目</span>
            </div>
            <span className={`pmd-summary-card__value${summary.risk > 0 ? ' pmd-summary-card__value--risk' : ''}`}>
              {summary.risk}
            </span>
          </div>
        </Col>
      </Row>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="pmd-monitor-tabs"
      />
    </div>
  );
};

export default ProjectMonitorDetail;
